import log from '@/logger';

import constants from '@/constants';

import { namespacedGetters as companyFilesGetters } from '@/store/companyFiles/getterTypes';
import getSearchRegExp from '@/utils/getSearchRegExp';

export const managingCompany = (state) => {
  if (!state.companies) {
    return {};
  }

  const company = Object.values(state.companies).find(({ managingCompany }) => managingCompany === true);
  return company || {};
};

const companyFilters = {
  byArchived: ({ archived }) => !archived,

  byCity: (city) => {
    const value = city.toLowerCase();
    return ({ address }) => !!(address && address.city && value === address.city.toLowerCase());
  },

  // Unused for now
  byCountry: (country) => {
    const value = country.toLowerCase();
    return (company) =>
      !!(company.address && company.address.country && value === company.address.country.toLowerCase());
  },

  byPersons: (filteredPersons) => (company) => filteredPersons[company._id] != null,

  byOccupation: (occupation) => {
    const value = occupation.toLowerCase();
    return (company) =>
      company.mainBusiness.some((item) => !!item && !Array.isArray(item) && item.toLowerCase() === value);
  },

  byMissingUeFile: (companyFiles, getters) => {
    const getCompanyFilesByCompanyId = getters[companyFilesGetters.byCompanyId];
    const ids = companyFiles ? companyFiles.map(({ companyId }) => companyId) : [];

    return ({ _id, registrationLinks }) => {
      if (ids.includes(_id)) {
        return true;
      }

      if (!registrationLinks || !registrationLinks.length) {
        return false;
      }

      const companyFiles = getCompanyFilesByCompanyId(_id);

      return (
        !companyFiles ||
        !companyFiles.projectFiles ||
        registrationLinks.some(({ projectId }) => !companyFiles.projectFiles[projectId])
      );
    };
  },

  byCodeOfConduct: (matchingCompanyFiles, status, getters) => {
    const getCompanyFilesByCompanyId = getters[companyFilesGetters.byCompanyId];
    const ids = matchingCompanyFiles ? matchingCompanyFiles.map(({ companyId }) => companyId) : [];

    if (status !== constants.companyFiles.codeOfConductStatuses.UNKNOWN) {
      return ({ _id }) => ids.includes(_id);
    }

    return ({ _id, registrationLinks }) => {
      if (ids.includes(_id)) {
        return true;
      }

      const companyFiles = getCompanyFilesByCompanyId(_id);

      return (
        !companyFiles ||
        !companyFiles.projectFiles ||
        registrationLinks.some(({ projectId }) => !companyFiles.projectFiles[projectId])
      );
    };
  },

  byText: (text) => {
    const regexp = getSearchRegExp(text);
    return (company) => regexp.test(company.companyName) || regexp.test(company.mainBusiness.join(' '));
  },
};

const sortCompanies = (companies) => {
  return companies.sort((a, b) => {
    const at = a.companyName.toLowerCase();
    const bt = b.companyName.toLowerCase();
    if (at < bt) {
      return -1;
    } else if (at > bt) {
      return 1;
    }

    /** @todo Ids are alpha-numeric almost random strings. Wouldn't it be better to sort by 'createdAt'? */
    if (a._id < b._id) {
      return -1;
    }

    return 1;
  });
};

export const companies = (state) => {
  return state.companies ? sortCompanies(Object.values(state.companies).filter(companyFilters.byArchived)) : [];
};

export const companiesByOccupation = (state) => (values) => {
  if (!state.companies) {
    return [];
  }

  const filters = [companyFilters.byArchived];
  values && values.occupation && filters.push(companyFilters.byOccupation());
  const items = Object.values(state.companies).filter((company) => filters.every((filter) => filter(company)));

  return sortCompanies(items);
};

export const companiesByFilter = (state, getters) => (values) => {
  if (!state.companies) {
    return { items: [], matchByPersons: [] };
  }

  const filteredPersons = getters.persons(values);
  const preFilters = [companyFilters.byArchived];
  const filters = [];

  const hasPersons = companyFilters.byPersons(filteredPersons);
  if (values) {
    values.projectId && filters.push(hasPersons);
    values.city && filters.push(companyFilters.byCity(values.city));
    /** @todo Country filter isn't used across the site. */
    values.country && filters.push(companyFilters.byCountry(values.country));
    values.occupation && filters.push(companyFilters.byOccupation(values.occupation));
    values.text && filters.push(companyFilters.byText(values.text));
    if (values.withMissingUeFile) {
      const companyFiles = getters[companyFilesGetters.withMissingUeFile];
      filters.push(companyFilters.byMissingUeFile(companyFiles, getters));
    }
    if (values.withCodeOfConductStatus) {
      const matchingCompanyFiles = getters[companyFilesGetters.withCodeOfConductStatus](values.withCodeOfConductStatus);
      filters.push(companyFilters.byCodeOfConduct(matchingCompanyFiles, values.withCodeOfConductStatus, getters));
    }
  }

  const matchByPersons = [];
  const items = Object.values(state.companies).filter((company) => {
    if (!preFilters.every((filter) => filter(company))) {
      return false;
    }

    if (filters.every((filter) => filter(company))) {
      return true;
    }

    if (!company.archived && hasPersons(company)) {
      matchByPersons.push(company._id);
      return true;
    }

    return false;
  });

  return { items: sortCompanies(items), matchByPersons };
};

export const cities = (state) => {
  if (!state.companies) {
    return [];
  }

  const items = {};
  Object.values(state.companies).forEach(({ archived, address }) => {
    if (archived || !address || !address.city) {
      return;
    }

    const key = address.city.toLowerCase();
    if (key && !items[key]) {
      items[key] = address.city;
    }
  });

  return Object.entries(items)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, city]) => city);
};

/**
 * @todo Seems like this getter is unused. It is imported only in one legacy
 * component that should be removed and in one test file.
 */
export const countries = (state) => {
  if (!state.companies) {
    return [];
  }

  const items = {};
  Object.values(state.companies).forEach(({ archived, address }) => {
    if (archived || !address || !address.country) {
      return;
    }

    const key = address.country.toLowerCase();
    if (key && !items[key]) {
      items[key] = address.country;
    }
  });

  return Object.entries(items)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, country]) => country);
};

export const getCompany = (state) => (id) => {
  const company = state.companies[id];
  if (company) {
    return company;
  }

  if (id != null) {
    /** @todo Do we really need to log it somewhere? */
    log.error('companyGetters:getCompany.not found', id);
  }

  return { companyName: `Unknown: ${id}`, _id: id };
};

export const companyAndPersonName = (state) => (id) => {
  const person = state.persons[id];
  if (!person) {
    return '';
  }

  const name = [[person.firstName, person.lastName].filter(Boolean).join(' ')];
  const company = state.companies[person.companyId];
  if (company) {
    name.unshift(company.companyName);
  }

  return name.filter(Boolean).join(', ');
};



// WEBPACK FOOTER //
// ./src/store/company/companyGetters.js